import React from 'react'
import { CompetitionGroup } from '@plvs/graphql/generated'
import { Box } from '@plvs/respawn/features/layout'
import { NxRandomFirebobHover } from '@playvs-inc/nexus-misc-assets'
import { NxTypography } from '@playvs-inc/nexus-components'
import { useDebounce } from 'use-debounce'
import { makeStyles } from '@material-ui/core'
import { CreateCSSProperties } from '@material-ui/styles'

interface StyleProps {
  isHovered?: boolean
}

const useStyles = makeStyles((theme) => ({
  title: {
    fontSize: '2.8rem',
    fontWeight: 700,
    letterSpacing: '-1px',
    lineHeight: '67px',
    color: theme.palette.ColorTextBase,
  },
  dashboardIconContainer: {
    width: 68,
    height: 68,
  },
  playVsLogo: ({ isHovered }: StyleProps): CreateCSSProperties => ({
    transition: 'transform 0.40s ease-in-out',
    transform: isHovered ? 'translateX(5px)' : 'translateX(0px)',
  }),
  img: {
    marginBottom: theme.spacing(2),
  },
  collegeImg: {
    height: 50,
    width: 165,
  },
}))

interface Props {
  competitionGroup: CompetitionGroup | null | undefined
  isDarkMode: boolean
}

export const OrgTypeLogo: React.FC<Props> = ({
  competitionGroup,
  isDarkMode,
}) => {
  const [isHovered, setIsHovered] = React.useState(false)
  const [debouncedIsHovered] = useDebounce(isHovered, 100)

  const classes = useStyles({ isHovered: debouncedIsHovered })

  switch (competitionGroup) {
    case CompetitionGroup.College:
      return (
        <img
          alt="college-logo"
          className={`${classes.img} ${classes.collegeImg}`}
          data-testid="college-logo"
          src={
            isDarkMode
              ? 'https://s3.us-east-1.amazonaws.com/assets.playvs.com/PlayVS/PlayVS-College-Logo-Dark.svg'
              : 'https://s3.us-east-1.amazonaws.com/assets.playvs.com/PlayVS/PlayVS-College-Logo.svg'
          }
        />
      )
    case CompetitionGroup.HighSchool:
      return (
        <img
          alt="high-school-logo"
          className={classes.img}
          data-testid="high-school-logo"
          src={
            isDarkMode
              ? 'https://s3.amazonaws.com/assets.playvs.com/PlayVS/PlayVS-HS-Logo-Dark.svg'
              : 'https://s3.amazonaws.com/assets.playvs.com/PlayVS/PlayVS-HS-Logo.svg'
          }
        />
      )
    case CompetitionGroup.MiddleSchool:
      return (
        <img
          alt="middle-school-logo"
          className={classes.img}
          data-testid="middle-school-logo"
          src={
            isDarkMode
              ? 'https://s3.amazonaws.com/assets.playvs.com/PlayVS/PlayVS-MS-Logo-Dark.svg'
              : 'https://s3.amazonaws.com/assets.playvs.com/PlayVS/PlayVS-MS-Logo.svg'
          }
        />
      )
    default:
      return (
        <Box
          display="flex"
          flexDirection="row"
          mb={2}
          onMouseEnter={(): void => setIsHovered(true)}
          onMouseLeave={(): void => setIsHovered(false)}
        >
          <Box className={classes.dashboardIconContainer}>
            <NxRandomFirebobHover
              height={68}
              isHovered={debouncedIsHovered}
              overrideHover
              width={68}
            />
          </Box>
          <Box className={classes.playVsLogo} data-testid="standard-logo">
            <NxTypography className={classes.title}>PlayVS</NxTypography>
          </Box>
        </Box>
      )
  }
}
